import React, {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import {
	AutoComplete,
	Card,
	Col,
	ConfigProvider,
	Flex,
	Input,
	Modal as AntdModal,
	Row,
	Space,
} from 'antd';
import isEqual from 'lodash.isequal';
import uuid from 'short-uuid';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
	faAdd,
	faArrowRight,
	faGasPump,
	faRefresh,
	faRightLeft,
} from '@fortawesome/pro-light-svg-icons';
import { faCalculator } from '@fortawesome/pro-solid-svg-icons';
import { faPencil } from '@fortawesome/pro-regular-svg-icons';
import classNames from 'classnames';
import { useQuery } from 'react-query';
import {
	CopyOutlined,
	DeleteOutlined,
	PlusOutlined,
} from '@ant-design/icons';
import { Moment } from 'moment';
import {
	CargoContractSides,
	CargoUnitLabels,
	CargoUnitTypes,
	CurrencySymbols,
	EstimateStatus,
	FreightRateType,
	LaytimeIntervals,
	LaytimeTerms,
	TimeZoneFormat,
	VesselConditionTypes,
	VesselCostType,
	VesselOwnershipTypes,
} from '@shared/utils/constants';
import { round } from '@shared/utils/math';
import { PortRotationEntryGeneratorTypes } from '@shared/utils/port-rotation-entry-generator-types';
import { formatCurrency } from '@shared/utils/currency';
import { sortByDates } from '@shared/utils/sortByDates';
import { capitalize } from '@shared/utils/string';
import {
	fromISOTimezone,
	timeZoneOffsetLabel,
	toISOTimezoneInstant,
} from '@shared/utils/timezones';
import type { EstimateBunker } from '@api/features/estimates/getEstimateDetails';
import type { UpdateEstimateAttributes } from '@api/features/estimates/updateEstimate';
import type { EditableCargo } from '@api/features/cargos/updateCargo';
import type { EstimatesWithResults } from '@api/features/estimates/getEstimateGroup';
import type { CargoProps } from '@api/models/cargo';
import MatrixTable from '@client/components/Table/MatrixTable';
import Select from '@client/components/Select';
import {
	createCargo,
	createCargoRelet,
	deleteCargo,
	deleteCargoRelet,
	deleteEstimate,
	getEstimateGroupVessels,
	getUserData,
	refreshEuaPrice,
	updateEstimate,
} from '@client/lib/api';
import DatePicker from '@client/components/DatePicker';
import showErrorNotification from '@client/utils/showErrorNotification';
import ChartLegend from '@client/components/ChartLegend';
import NumericInput from '@client/components/NumericInput';
import VesselSelector from '@client/components/VesselSelector';
import ConsumptionSets from '@client/screens/fleet/VesselDetailsScreen/components/ConsumptionSets/ConsumptionSets';
import { useNavigationBlock } from '@client/lib/navigationBlock';
import Button from '@client/components/Button';
import showSuccessNotification from '@client/utils/showSuccessNotification';
import CargoDetailsModal from '@client/screens/cargos/components/CargoDetailsModal/CargoDetailsModal';
import IntakeCalculatorModal from '@client/screens/cargos/components/IntakeCalculatorModal/IntakeCalculatorModal';
import EstimateCosts from '@client/screens/estimates/costs/EstimateCosts';
import CargoContractSideTag from '@client/screens/cargos/details/utils/CargoContractSideTag';
import Modal from '@client/components/Modal/Modal';
import {
	findVerticalTarget,
	useSpreadsheetNavigation,
} from '@client/utils/hooks/useSpreadsheetNavigation';
import SpreadsheetNavigationHint from '@client/components/SpreadsheetNavigationHint';
import { useCentralStore } from './context/CentralStoreContext';
import { useCargoProvider } from './context/CargoContext';
import styles from './styles/GridView.module.css';
import pnlStyles from './components/styles/EstimatedPnL.module.css';
import { useEstimate } from './context/EstimatorContext';
import { useEstimateChangesProvider } from './context/EstimateChangesContext';
import { useGridChanges } from './context/GridChangesContext';
import { calcTotal } from './context/hooks/useMiscEstimateCostHandler';
import { getCommencementBunkerValues } from './helpers/getCommencementBunkerValues';
import { confirmLaytimeTermsChange } from './helpers/confirmLaytimeTermsChange';
import { formatFreightRate } from './helpers/formatFreightRate';
import TargetControls from './components/TargetControls';
import {
	getCargoForField,
	isSyncedReletCargoField,
	updateCargoAndRelet,
} from './helpers/cargoCardColumns';
import RobsAndPricesModal from './components/RobsAndPricesModal';
import LinkContractEstimateModal from './components/LinkContractEstimateModal';
import PortsModal from './components/PortsModal';

type GridEstimate = EstimatesWithResults[number];

type EstimateModalType = 'ports' | 'intake' | 'miscCosts';

const SAVE_FIRST_TOOLTIP = 'Save your changes first';

const sortGridCargos = <T extends Pick<CargoProps, 'id' | 'contractSide'>>(cargos: T[]) => (
	[...cargos].sort((a, b) => (
		Number(a.contractSide === CargoContractSides.VC_IN) -
		Number(b.contractSide === CargoContractSides.VC_IN) ||
		a.id - b.id
	))
);

const GridView = () => {
	const [selectedVesselId, setSelectedVesselId] = useState<number | null>(null);
	const [blockClose, setBlockClose] = useState(false);
	const [showRobsModal, setShowRobsModal] = useState(false);
	const [showLinkModal, setShowLinkModal] = useState(false);
	const [editingCargoId, setEditingCargoId] = useState<number | null>(null);
	const [estimateModal, setEstimateModal] = useState<{
		type: EstimateModalType;
		estimateId: number;
	} | null>(null);
	const [refreshingEuaEstimateId, setRefreshingEuaEstimateId] = useState<number | null>(null);
	const [editEstimateCostMode, setEditEstimateCostMode] = useState(false);
	const navigationProps = useSpreadsheetNavigation();
	const portsTriggerRef = useRef<HTMLElement | null>(null);

	// Used for resetting commencement bunkers in case the user closes the
	// ROB modal without saving
	const [originalRobValues, setOriginalRobValues] = useState<Array<EstimateBunker> | undefined>();

	const {
		estimate,
		onUpdateEstimate: onUpdateSelectedEstimate,
		estimateCosts,
		refreshEstimateCosts,
	} = useEstimate();

	const {
		saving,
		onSaveChanges,
		hasPendingChanges,
		onClearPendingChanges,
	} = useEstimateChangesProvider();

	const {
		pendingEstimateChanges,
		pendingCargoChanges,
		pendingBallastLegChanges,
		onUpdateGridEstimate,
		onUpdateGridCargo,
		onUpdateGridBallastLeg,
		hasPendingGridChanges,
	} = useGridChanges();

	const { componentDisabled } = ConfigProvider.useConfig();

	const {
		estimates,
		refreshEstimateGroup,
		setLoading,
		refreshEverything,
		handleCreateSubEstimate,
		setSelectedEstimateId,
	} = useCentralStore();

	const groupVesselIds = [...new Set((estimates ?? []).map((e) => e.vesselId))];

	const {
		data: estimateGroupVessels,
		refetch: refreshEstimateGroupVessels,
		isFetching: fetchingGroupVessels,
	} = useQuery(
		['estimateGroupVessels', estimate?.estimateGroupId, groupVesselIds],
		() => getEstimateGroupVessels(estimate?.estimateGroupId!),
		{
			retry: false,
			enabled: estimate != null,
		},
	);

	const {
		data: userData,
	} = useQuery(
		['userData'],
		getUserData,
		{
			retry: false,
		},
	);

	const vesselType = estimate?.vessel?.type;

	const cargoTypes = useMemo(() => {
		if (userData == null) {
			return [];
		}

		const activeCargoTypes = userData.cargoTypes.filter((cargoType) => (
			cargoType.active
		));

		if (vesselType == null) {
			return [...activeCargoTypes].sort((a, b) => (
				a.name.localeCompare(b.name)
			));
		}

		return activeCargoTypes
			.filter((cargoType) => (
				cargoType.vesselTypes?.includes(vesselType) ?? false
			))
			.sort((a, b) => a.name.localeCompare(b.name));
	}, [userData, vesselType]);

	// Simplified selectedVessel logic
	const selectedVessel = useMemo(() => {
		if (!selectedVesselId || !estimateGroupVessels) {
			return null;
		}

		const vessel = estimateGroupVessels.find((v) => v.id === selectedVesselId);

		if (!vessel) {
			return null;
		}

		const inPortEntries = vessel.PerformanceEntries.filter(
			({ condition }) => condition === VesselConditionTypes.LOADING ||
				condition === VesselConditionTypes.DISCHARGING ||
				condition === VesselConditionTypes.IDLE,
		);

		return {
			...vessel,
			inPortEntries,
			performanceEntries: vessel.PerformanceEntries,
			otherPerformanceEntries: vessel.OtherPerformanceEntries,
			consumptionSets: vessel.ConsumptionSets,
		};
	}, [estimateGroupVessels, selectedVesselId]);

	const {
		useBlocker,
		makeBlockable,
	} = useNavigationBlock();

	useBlocker(blockClose);
	useBlocker(showRobsModal && hasPendingChanges, 'You have unsaved changes. Are you sure you want to close?');

	const onCloseModal = makeBlockable(() => setSelectedVesselId(null));

	const onCloseLinkModal = makeBlockable(() => setShowLinkModal(false));

	const onCloseRobsModal = makeBlockable(() => {
		setShowRobsModal(false);
		onUpdateSelectedEstimate({
			// Remove ID
			commencementBunkers: originalRobValues?.map((r) => ({
				quantity: r.quantity,
				pricePerTon: r.pricePerTon,
				fuelGrade: r.fuelGrade,
			})),
		});
		onClearPendingChanges();
	});

	const openEstimateModal = (type: EstimateModalType, estimateId: number) => {
		setSelectedEstimateId(estimateId);
		setEstimateModal({ type, estimateId });
	};

	const closeEstimateModal = () => setEstimateModal(null);

	const closePortsModal = () => {
		closeEstimateModal();
		requestAnimationFrame(() => {
			const cell = portsTriggerRef.current?.closest('td');

			if (cell != null) {
				findVerticalTarget(cell, 1)?.focus();
			}
		});
	};

	const isEstimateModalOpen = (type: EstimateModalType) => (
		estimateModal?.type === type &&
		estimate?.id === estimateModal.estimateId
	);

	const onCloseMiscCostsModal = makeBlockable(async () => {
		if (editEstimateCostMode) {
			AntdModal.warning({
				title: 'Editing in progress',
				content: 'Please save or cancel your changes before closing.',
			});

			return;
		}

		closeEstimateModal();
		await refreshEverything();
	});

	// Synchronize local bunkers in case the estimate changes elsewhere
	useEffect(() => {
		if (
			originalRobValues &&
			estimate?.commencementBunkers &&
			!hasPendingChanges &&
			!isEqual(originalRobValues, estimate?.commencementBunkers)
		) {
			setOriginalRobValues(estimate?.commencementBunkers.map((c) => ({
				...c,
				id: uuid.generate(),
			})));
		}

		if (originalRobValues || !estimate?.commencementBunkers) {
			return;
		}

		setOriginalRobValues(estimate.commencementBunkers.map((b) => ({
			...b,
			id: uuid.generate(),
		})));
	}, [estimate?.commencementBunkers]);

	const {
		portOptions,
		charterers,
		setReletViewByEstimateId,
	} = useCargoProvider();

	const saveEstimateNow = useCallback(async (
		estimateId: number,
		attributes: UpdateEstimateAttributes,
	) => {
		setLoading(true);

		try {
			await updateEstimate(estimateId, attributes, false, false);
			await refreshEverything();
		} catch (e) {
			showErrorNotification('Could not update estimate', e as Error);
		} finally {
			setLoading(false);
		}
	}, [refreshEverything, setLoading]);

	const handleDeleteEstimate = useCallback(async (estimateId: number) => {
		setLoading(true);

		try {
			await deleteEstimate(estimateId);
			await refreshEstimateGroup();
			setSelectedEstimateId(estimates?.[0]?.id ?? null);
			showSuccessNotification('Estimate deleted successfully');
		} catch (e) {
			showErrorNotification('Could not delete estimate', e as Error);
		} finally {
			setLoading(false);
		}
	}, [refreshEstimateGroup, estimates, setSelectedEstimateId, setLoading]);

	const onRefreshEuaPrice = async (estimateId: number) => {
		setRefreshingEuaEstimateId(estimateId);

		try {
			await refreshEuaPrice(estimateId);
			showSuccessNotification('EUA price refreshed');
			await refreshEverything();
		} catch (e) {
			showErrorNotification('Could not refresh EUA price', e as Error);
		} finally {
			setRefreshingEuaEstimateId(null);
		}
	};

	const onSetTarget = async (
		estimateId: number,
		target: { targetTce: number } | { targetNetResult: number },
	) => {
		await saveEstimateNow(estimateId, target);
		showSuccessNotification('targetTce' in target ? 'Target TCE updated' : 'Target Net Result updated');
	};

	const onLockTarget = async (estimateId: number, target: 'tce' | 'netResult') => {
		const selectedEstimate = estimates?.find((e) => e.id === estimateId);

		if (selectedEstimate == null) {
			return;
		}

		if (target === 'tce') {
			const newState = !selectedEstimate.isTceLocked;

			await saveEstimateNow(estimateId, {
				isTceLocked: newState,
				targetTce: newState ? selectedEstimate.EstimateResult.tce : null,
			});

			return;
		}

		const newState = !selectedEstimate.isNetResultLocked;

		await saveEstimateNow(estimateId, {
			isNetResultLocked: newState,
			targetNetResult: newState ? selectedEstimate.EstimateResult.netProfit : null,
		});
	};

	const gridEstimates = (estimates ?? []).map((e) => ({
		...e,
		...pendingEstimateChanges[e.id],
		gridCargos: sortGridCargos(e.Cargos ?? []).map((c) => ({
			...c,
			...pendingCargoChanges[c.id],
		})),
	}));

	type GridRow = typeof gridEstimates[number];
	type GridCargo = GridRow['gridCargos'][number];

	const maxCargoCount = Math.max(0, ...gridEstimates.map((e) => e.gridCargos.length));

	const bunkerFuelGrades = [
		...new Set(gridEstimates.flatMap((e) => (
			(e.commencementBunkers ?? []).map((b) => b.fuelGrade)
		))),
	];

	const onChangeCargo = (row: GridRow, cargo: GridCargo, attributes: Partial<EditableCargo>) => {
		if (Object.keys(attributes).some(isSyncedReletCargoField)) {
			updateCargoAndRelet({
				sourceCargo: cargo,
				cargos: row.gridCargos,
				attributes,
				onUpdateCargo: onUpdateGridCargo,
			});

			return;
		}

		onUpdateGridCargo(cargo.id, attributes);
	};

	const getCargoValue = <K extends keyof GridCargo>(row: GridRow, cargo: GridCargo, field: K) => (
		getCargoForField(cargo, row.gridCargos, field)[field]
	);

	const isSyncedFromVcOut = (row: GridRow, cargo: GridCargo, field: keyof GridCargo) => (
		getCargoForField(cargo, row.gridCargos, field).id !== cargo.id
	);

	const onCreateCargo = async (estimateId: number) => {
		setLoading(true);

		try {
			await createCargo({
				estimateId,
				attributes: {
					type: 'new cargo',
				},
			});
			await refreshEverything();
		} catch (e) {
			showErrorNotification('Could not create cargo', e as Error);
		} finally {
			setLoading(false);
		}
	};

	const onCreateCargoRelet = async (estimateId: number) => {
		setLoading(true);

		try {
			await createCargoRelet({ estimateId });
			setReletViewByEstimateId((prev) => ({
				...prev,
				[estimateId]: true,
			}));
			await refreshEverything();
		} catch (e) {
			showErrorNotification('Failed to create cargo relet', e as Error);
		} finally {
			setLoading(false);
		}
	};

	const onDeleteCargo = async (row: GridRow, cargo: GridCargo) => {
		setLoading(true);

		try {
			const remove = hasCargoRelet(row) ? deleteCargoRelet : deleteCargo;

			if (cargo.voyageId != null) {
				await remove(cargo.id);
			} else {
				await remove(cargo.id, row.id, false);
			}

			showSuccessNotification('Cargo removed');
			await refreshEverything();
		} catch (e) {
			showErrorNotification('Failed to remove cargo', e as Error);
		} finally {
			setLoading(false);
		}
	};

	const onChangeCommencementBunker = (
		row: GridRow,
		fuelGrade: string,
		attributes: Partial<EstimateBunker>,
	) => {
		const commencementBunkers = (row.commencementBunkers ?? []).map((estimateBunker) => {
			if (estimateBunker.fuelGrade === fuelGrade) {
				const res = {
					...estimateBunker,
					...attributes,
				};

				delete res.id;

				return res;
			}

			return estimateBunker;
		});

		onUpdateGridEstimate(row.id, { commencementBunkers });
	};

	const getBallastLeg = (row: Pick<GridEstimate, 'EstimatePortRotationEntries'>) => row.EstimatePortRotationEntries.find((pr) => (
		pr.generatedBy === PortRotationEntryGeneratorTypes.BALLAST_LEG
	));

	const isFixedRow = (row: Pick<GridEstimate, 'status'>) => row.status === EstimateStatus.Fixed || componentDisabled;

	const hasCargoRelet = (row: Pick<GridEstimate, 'Cargos'>) => (
		(row.Cargos ?? []).some((c) => c.contractSide === CargoContractSides.VC_IN)
	);

	const isTargetDisabled = (row: Pick<GridEstimate, 'status' | 'Cargos'>) => (
		isFixedRow(row) ||
		hasPendingGridChanges ||
		hasCargoRelet(row)
	);

	const currencySymbol = (row: Pick<GridEstimate, 'currency'>) => (
		CurrencySymbols[row?.currency as keyof typeof CurrencySymbols] ?? row?.currency
	);

	const getRowVessel = (row: Pick<GridEstimate, 'vesselId'>) => (
		estimateGroupVessels?.find((v) => v.id === row.vesselId)
	);

	const getVesselParticular = (
		row: Pick<GridEstimate, 'vesselId' | 'summerDraftOverride' | 'dwtOverride' | 'tpcOverride'>,
		overrideKey: 'summerDraftOverride' | 'dwtOverride' | 'tpcOverride',
		vesselKey: 'summerDraft' | 'summerDWT' | 'TPC',
	) => (row[overrideKey] ?? getRowVessel(row)?.[vesselKey]) || undefined;

	const renderResult = (value: number, row: Pick<GridEstimate, 'currency'>) => (
		<b
			className={classNames({
				[pnlStyles.green]: value > 0,
				[pnlStyles.red]: value < 0,
			})}
		>
			{formatCurrency(value, row?.currency ?? 'USD')}
		</b>
	);

	const getCargoGroup = (index: number) => {
		const cargosAtIndex = gridEstimates.flatMap((e) => e.gridCargos[index] ?? []);

		const hasLaytimeTerms = (...terms: LaytimeTerms[]) => cargosAtIndex.some((c) => (
			c.laytimeTerms != null && terms.includes(c.laytimeTerms)
		));

		const cargoRow = (
			title: string,
			field: keyof GridCargo,
			renderCargo: (cargo: GridCargo, row: GridRow) => React.ReactNode,
		) => ({
			title,
			getValue: (row: GridRow) => {
				const cargo = row.gridCargos[index];

				return cargo == null ? undefined : getCargoValue(row, cargo, field);
			},
			render: (_v: unknown, row: GridRow) => {
				const cargo = row.gridCargos[index];

				return cargo == null ? null : renderCargo(cargo, row);
			},
		});

		const laytimeIntervalOptions = Object.values(LaytimeIntervals).map((interval) => ({
			label: capitalize(interval),
			value: interval,
		}));

		const renderTimeAllowed = (
			field: 'timeAllowed' | 'timeAllowedLoading' | 'timeAllowedDischarging',
			terms: LaytimeTerms,
		) => (cargo: GridCargo, row: GridRow) => {
			if (cargo.laytimeTerms !== terms) {
				return null;
			}

			const value = cargo[field];

			return (
				<NumericInput
					disabled={isFixedRow(row)}
					size="small"
					value={value == null ? null : Number(value)}
					addonAfter={cargo.timeAllowedInterval}
					onChange={(e) => onChangeCargo(row, cargo, { [field]: e })}
				/>
			);
		};

		return {
			title: maxCargoCount > 1 ? `Cargo ${index + 1}` : 'Cargo',
			outlined: true,
			children: [
				cargoRow('Commodity', 'type', (cargo, row) => {
					const isRelet = hasCargoRelet(row);
					const showActions = !isRelet || cargo.contractSide === CargoContractSides.VC_IN;

					return (
						<Flex gap={4} align="center">
							{isRelet && (
								<CargoContractSideTag contractSide={cargo.contractSide} />
							)}
							<AutoComplete
								className={styles.fullWidth}
								disabled={isFixedRow(row)}
								data-shortcut="cargo-commodity"
								value={cargo.type}
								placeholder="Cargo description"
								size="small"
								onChange={(value) => onChangeCargo(row, cargo, { type: value })}
								onSelect={(value: string) => {
									const cargoType = cargoTypes.find((ct) => ct.name === value);

									if (cargoType?.stowageFactorCBFT != null) {
										onChangeCargo(row, cargo, { stowageFactor: cargoType.stowageFactorCBFT });
									}
								}}
								filterOption={(inputValue, option) => (
									option?.label
										?.toString()
										.toUpperCase()
										.includes(inputValue.toUpperCase()) ?? false
								)}
								options={cargoTypes.map((cargoType) => ({
									label: cargoType.name,
									value: cargoType.name,
								}))}
							/>
							{showActions && (
								<>
									<Button
										type="link"
										size="small"
										disabled={hasPendingGridChanges}
										disabledTooltip={SAVE_FIRST_TOOLTIP}
										onClick={() => setEditingCargoId(cargo.id)}
										icon={(<FontAwesomeIcon icon={faPencil} />)}
										data-testid="openCargo"
									/>
									<Button
										type="link"
										size="small"
										danger
										disabled={isFixedRow(row) || hasPendingGridChanges}
										disabledTooltip={hasPendingGridChanges ? SAVE_FIRST_TOOLTIP : undefined}
										confirmTitle={isRelet ?
											(
												'Are you sure you want to undo the cargo relet?\n\n' +
												'This will remove the VC-IN cargo and restore the VC-OUT cargo.'
											) :
											'Are you sure you want to remove this cargo from the estimate?'
										}
										onClick={() => onDeleteCargo(row, cargo)}
										icon={(<DeleteOutlined />)}
										data-testid="deleteCargo"
									/>
								</>
							)}
						</Flex>
					);
				}),
				cargoRow('Loadport(s)', 'loadingPorts', (cargo, row) => (
					<Select
						disabled={isFixedRow(row) || isSyncedFromVcOut(row, cargo, 'loadingPorts')}
						size="small"
						value={getCargoValue(row, cargo, 'loadingPorts').map((lp) => (
							typeof lp === 'object' ? lp.id : lp
						))}
						// @ts-ignore This works because it's feature in rc-select
						showAction="focus"
						mode="multiple"
						placeholder="Loading Ports"
						options={portOptions}
						onChange={(e) => onChangeCargo(row, cargo, { loadingPorts: e ?? [] })}
					/>
				)),
				cargoRow('Disport(s)', 'dischargePorts', (cargo, row) => (
					<Select
						disabled={isFixedRow(row) || isSyncedFromVcOut(row, cargo, 'dischargePorts')}
						size="small"
						value={getCargoValue(row, cargo, 'dischargePorts').map((dp) => (
							typeof dp === 'object' ? dp.id : dp
						))}
						// @ts-ignore This works because it's feature in rc-select
						showAction="focus"
						mode="multiple"
						placeholder="Discharge Ports"
						options={portOptions}
						onChange={(e) => onChangeCargo(row, cargo, { dischargePorts: e ?? [] })}
					/>
				)),
				cargoRow('Qty', 'quantity', (cargo, row) => (
					<NumericInput
						disabled={isFixedRow(row) || isSyncedFromVcOut(row, cargo, 'quantity')}
						size="small"
						value={getCargoValue(row, cargo, 'quantity')}
						placeholder="Quantity"
						addonAfter={CargoUnitLabels[getCargoValue(row, cargo, 'unit')]}
						onChange={(e) => onChangeCargo(row, cargo, { quantity: Number(e) })}
					/>
				)),
				cargoRow('Tolerance', 'quantityTolerance', (cargo, row) => (
					<NumericInput
						disabled={isFixedRow(row) || isSyncedFromVcOut(row, cargo, 'quantityTolerance')}
						size="small"
						value={getCargoValue(row, cargo, 'quantityTolerance')}
						placeholder="Tolerance"
						addonAfter="%"
						onChange={(e) => onChangeCargo(row, cargo, { quantityTolerance: Number(e) })}
					/>
				)),
				cargoRow('Units', 'unit', (cargo, row) => (
					<Select
						disabled={isFixedRow(row) || isSyncedFromVcOut(row, cargo, 'unit')}
						value={getCargoValue(row, cargo, 'unit')}
						size="small"
						// @ts-ignore This works because it's feature in rc-select
						showAction="focus"
						placeholder="Unit"
						options={Object.keys(CargoUnitTypes).map((u) => ({
							label: CargoUnitLabels[u as keyof typeof CargoUnitLabels],
							value: u,
						}))}
						onChange={(e) => onChangeCargo(row, cargo, { unit: e ?? undefined })}
					/>
				)),
				cargoRow('Rate', 'freightRate', (cargo, row) => (
					<NumericInput
						disabled={row.isTceLocked || row.isNetResultLocked || isFixedRow(row)}
						size="small"
						value={cargo.freightRate}
						placeholder="Freight Rate"
						addonBefore={currencySymbol(row)}
						addonAfter={`/${CargoUnitLabels[getCargoValue(row, cargo, 'unit')]}`}
						onChange={(e) => onChangeCargo(row, cargo, { freightRate: Number(e) })}
					/>
				)),
				cargoRow('Freight Type', 'freightType', (cargo, row) => {
					const unit = getCargoValue(row, cargo, 'unit');

					return (
						<Select
							disabled={isFixedRow(row)}
							value={cargo.freightType}
							size="small"
							// @ts-ignore This works because it's feature in rc-select
							showAction="focus"
							placeholder="Unit"
							options={[
								{
									label: 'Lumpsum',
									value: FreightRateType.LUMPSUM,
								},
								{
									label: unit != null ? `Per ${CargoUnitLabels[unit]}` : 'Per unit',
									value: FreightRateType.PER_UNIT,
								},
							]}
							onChange={(e) => onChangeCargo(row, cargo, { freightType: e ?? undefined })}
						/>
					);
				}),
				{
					...cargoRow('Intake', 'id', (_cargo, row) => (
						<Button
							size="small"
							disabled={
								isFixedRow(row) ||
								hasCargoRelet(row) ||
								row.gridCargos.length !== 1 ||
								hasPendingGridChanges
							}
							disabledTooltip={hasPendingGridChanges ? SAVE_FIRST_TOOLTIP : undefined}
							icon={(<FontAwesomeIcon icon={faCalculator} />)}
							onClick={() => openEstimateModal('intake', row.id)}
							data-testid="intakeCalculator"
						>
							Intake Calculator
						</Button>
					)),
					getValue: () => null,
				},
				cargoRow('Add. Com', 'addressCommission', (cargo, row) => (
					<NumericInput
						disabled={isFixedRow(row)}
						size="small"
						value={cargo.addressCommission}
						placeholder="Address Commission"
						addonAfter="%"
						onChange={(e) => onChangeCargo(row, cargo, { addressCommission: Number(e) })}
					/>
				)),
				cargoRow('Br. Com', 'brokerCommission', (cargo, row) => (
					<NumericInput
						disabled={isFixedRow(row)}
						size="small"
						value={cargo.brokerCommission}
						placeholder="Broker Commission"
						addonAfter="%"
						onChange={(e) => onChangeCargo(row, cargo, { brokerCommission: Number(e) })}
					/>
				)),
				cargoRow('Charterer', 'charterer', (cargo, row) => (
					<Select
						disabled={isFixedRow(row)}
						value={cargo.charterer === 0 ? undefined : cargo.charterer}
						size="small"
						// @ts-ignore This works because it's feature in rc-select
						showAction="focus"
						placeholder={cargo.contractSide === CargoContractSides.VC_IN ? 'Select Owner' : 'Select Charterer'}
						options={(charterers ?? []).map((c) => ({
							label: c.name,
							value: c.id,
						}))}
						onChange={(c) => onChangeCargo(row, cargo, { charterer: c })}
					/>
				)),
				cargoRow('Laytime type', 'laytimeTerms', (cargo, row) => (
					<Select
						disabled={isFixedRow(row)}
						value={cargo.laytimeTerms}
						size="small"
						// @ts-ignore This works because it's feature in rc-select
						showAction="focus"
						placeholder="Select Type"
						options={Object.values(LaytimeTerms).map((terms) => ({
							label: terms,
							value: terms,
						}))}
						onChange={(laytimeTerms) => {
							if (laytimeTerms == null || laytimeTerms === cargo.laytimeTerms) {
								return;
							}

							confirmLaytimeTermsChange(cargo, () => onChangeCargo(row, cargo, { laytimeTerms }));
						}}
						data-testid="laytimeTerms"
					/>
				)),
				...(hasLaytimeTerms(LaytimeTerms.REVERSIBLE, LaytimeTerms.AVERAGED) ? [
					cargoRow('Time allowed interval', 'timeAllowedInterval', (cargo, row) => (
						cargo.laytimeTerms === LaytimeTerms.REVERSIBLE || cargo.laytimeTerms === LaytimeTerms.AVERAGED ? (
							<Select
								disabled={isFixedRow(row)}
								value={cargo.timeAllowedInterval}
								size="small"
								// @ts-ignore This works because it's feature in rc-select
								showAction="focus"
								options={laytimeIntervalOptions}
								onChange={(interval) => onChangeCargo(row, cargo, {
									timeAllowedInterval: interval ?? undefined,
								})}
							/>
						) : null
					)),
				] : []),
				...(hasLaytimeTerms(LaytimeTerms.REVERSIBLE) ? [
					cargoRow('Total time allowed', 'timeAllowed', renderTimeAllowed('timeAllowed', LaytimeTerms.REVERSIBLE)),
				] : []),
				...(hasLaytimeTerms(LaytimeTerms.AVERAGED) ? [
					cargoRow(
						'Total time allowed loading',
						'timeAllowedLoading',
						renderTimeAllowed('timeAllowedLoading', LaytimeTerms.AVERAGED),
					),
					cargoRow(
						'Total time allowed discharging',
						'timeAllowedDischarging',
						renderTimeAllowed('timeAllowedDischarging', LaytimeTerms.AVERAGED),
					),
				] : []),
			],
		};
	};

	return (
		<Row gutter={[8, 8]}>
			<CargoDetailsModal
				mode={'estimate'}
				cargoId={editingCargoId}
				setEditingCargoId={setEditingCargoId}
				visible={editingCargoId != null}
				isTceLocked={false}
				refresh={refreshEverything}
			/>
			{isEstimateModalOpen('ports') && (
				<PortsModal onClose={closePortsModal} />
			)}
			{isEstimateModalOpen('intake') && (
				<IntakeCalculatorModal
					visible
					setVisible={closeEstimateModal}
					cargoTypes={cargoTypes}
				/>
			)}
			{estimate != null && (
				<Modal
					open={isEstimateModalOpen('miscCosts')}
					footer={null}
					onCancel={onCloseMiscCostsModal}
					width="75%"
					destroyOnClose
				>
					<EstimateCosts
						estimate={estimate}
						estimateCosts={estimateCosts}
						onUpdateEstimate={({ miscCostTotal }) => saveEstimateNow(estimate.id, { miscCostTotal })}
						refreshEstimateCosts={refreshEstimateCosts}
						setEditEstimateCostMode={setEditEstimateCostMode}
					/>
				</Modal>
			)}
			<Modal
				open={!!selectedVessel}
				footer={null}
				onCancel={onCloseModal}
				width="75%"
			>
				{selectedVessel && (
					<ConsumptionSets
						refreshVessel={refreshEstimateGroupVessels}
						activeConsumptionSetId={estimate?.activeConsumptionSetId}
						inPortEntries={selectedVessel.inPortEntries}
						setEditing={() => setSelectedVesselId(null)}
						onRefresh={refreshEstimateGroupVessels}
						setBlockClose={setBlockClose}
						// @ts-ignore
						vessel={selectedVessel}
					/>
				)}
			</Modal>
			<Modal
				title="ROBs & prices"
				open={showRobsModal}
				onCancel={onCloseRobsModal}
				onOk={() => setShowRobsModal(false)}
				cancelButtonProps={{
					style: {
						visibility: 'hidden',
					},
				}}
				okButtonProps={{
					style: {
						visibility: 'hidden',
					},
				}}
				width={600}
			>
				{estimate && (
					<RobsAndPricesModal
						onUpdateEstimate={onUpdateSelectedEstimate}
						estimate={estimate}
						onSaveChanges={() => {
							onSaveChanges();
							setOriginalRobValues(undefined);
							setShowRobsModal(false);
						}}
						saving={saving}
						hasPendingChanges={hasPendingChanges}
					/>
				)}
			</Modal>
			{estimate?.vesselId != null && (
				<Modal
					open={showLinkModal}
					footer={null}
					onCancel={onCloseLinkModal}
					okButtonProps={{
						onClick: onCloseLinkModal,
					}}
					width={500}
					destroyOnClose
				>
					<LinkContractEstimateModal />
				</Modal>
			)}
			<Col span={24}>
				<Flex justify="space-between" align="center">
					<ChartLegend
						position="flex-start"
						items={[
							{
								key: 'calculated',
								label: 'Calculated Field',
								fill: 'hsla(56, 100%, 50%, 0.2)',
							},
							{
								key: 'difference',
								label: 'Has difference',
								fill: 'rgba(0, 123, 255, 0.08)',
							},
						]}
					/>
					<SpreadsheetNavigationHint />
				</Flex>
				<Card className={styles.fitContent} styles={{ body: { padding: 0 } }}>
					<div
						style={{
							width: 300 + ((estimates ?? []).length * 275),
							overflowX: 'scroll',
						}}
						{...navigationProps}
					>
						<MatrixTable
							compare
							data={gridEstimates.map((e) => ({
								...e,
								label: (
									<div
										style={{
											display: 'flex',
											justifyContent: 'space-between',
											paddingLeft: 12,
											alignItems: 'center'
										}}
									>
										<div />
										<Space size={2}>
											<Button
												type="link"
												disabled={hasPendingGridChanges}
												disabledTooltip={SAVE_FIRST_TOOLTIP}
												icon={(<CopyOutlined />)}
												onClick={() => handleCreateSubEstimate({ estimateId: e.id })}
											/>
											<Button
												disabled={isFixedRow(e) || hasPendingGridChanges}
												disabledTooltip={hasPendingGridChanges ? SAVE_FIRST_TOOLTIP : undefined}
												type="link"
												danger
												icon={(<DeleteOutlined />)}
												onClick={() => handleDeleteEstimate(e.id)}
											/>
										</Space>
									</div>
								),
							}))}
							groups={[
								{
									title: 'Estimate',
									children: [
										{
											title: 'Name',
											render: (_, row) => (
												<Input
													disabled={isFixedRow(row)}
													value={row.name ?? ''}
													onChange={(e) => onUpdateGridEstimate(row.id, { name: e.target.value })}
												/>
											),
										},
										{
											title: 'Est. comment',
											key: 'comment',
											render: (comment, row) => (
												<Input
													disabled={isFixedRow(row)}
													value={comment || ''}
													placeholder="Est. comment"
													onChange={(e) => onUpdateGridEstimate(row.id, { comment: e.target.value })}
													data-testid="estimateComment"
												/>
											),
										},
									],
								},
								{
									title: 'P&L',
									children: [
										{ title: 'Freight', key: ['EstimateResult', 'freight'], type: 'currency', calculated: true },
										{ title: 'Port Costs', key: ['EstimateResult', 'portCharge'], type: 'currency', calculated: true },
										{
											title: 'Bunker Exp.',
											key: ['EstimateResult', 'bunkerExpense'],
											type: 'currency',
											calculated: true
										},
										{
											title: 'Other Costs',
											key: ['EstimateResult', 'otherVoyageExpenses'],
											type: 'currency',
											calculated: true
										},
										{ title: 'EU ETS', key: ['EstimateResult', 'totalEUAcost'], type: 'currency', calculated: true },
										{ title: 'UK ETS', key: ['EstimateResult', 'totalUKAcost'], type: 'currency', calculated: true },
										{ title: 'FuelEU', key: ['EstimateResult', 'fuelEuPenalty'], type: 'currency', calculated: true },
										{
											title: 'Net Profit',
											key: ['EstimateResult', 'voyageProfit'],
											type: 'currency',
											calculated: true
										},
										{
											title: (<b>Total Days</b>),
											key: ['EstimateResult', 'estimatedDuration'],
											type: 'number',
											calculated: true,
											render: (v) => (<b>{`${round(v, 2)} Days`}</b>),
										},
										{
											title: (<b>TCE /day</b>),
											key: ['EstimateResult', 'tce'],
											type: 'currency',
											calculated: true,
											render: (v, row) => (
												<Space>
													<TargetControls
														name="targetTce"
														label="Target TCE"
														shortcut="tce-target"
														isLocked={row.isTceLocked}
														target={row.targetTce}
														onLock={() => onLockTarget(row.id, 'tce')}
														onSetTarget={(targetTce) => onSetTarget(row.id, { targetTce })}
														disabled={isTargetDisabled(row) || row.isNetResultLocked}
														inputDisabled={row.Cargos.length === 0}
													/>
													<b>{formatCurrency(v, row?.currency ?? 'USD')}</b>
												</Space>
											),
										},
										{
											title: 'Vessels Costs',
											key: ['EstimateResult', 'tcInExpense'],
											type: 'currency',
											calculated: true
										},
										{
											title: (<b>Net Result</b>),
											key: ['EstimateResult', 'netProfit'],
											type: 'currency',
											calculated: true,
											render: (v, row) => (
												<Space>
													<TargetControls
														name="targetNetResult"
														label="Target Net Result"
														isLocked={row.isNetResultLocked}
														target={row.targetNetResult}
														onLock={() => onLockTarget(row.id, 'netResult')}
														onSetTarget={(targetNetResult) => onSetTarget(row.id, { targetNetResult })}
														disabled={isTargetDisabled(row) || row.isTceLocked}
														inputDisabled={row.Cargos.length === 0}
													/>
													{renderResult(v, row)}
												</Space>
											),
										},
										{
											title: (<b>Net Result /day</b>),
											key: ['EstimateResult', 'netProfitPerDay'],
											type: 'currency',
											calculated: true,
											render: renderResult,
										},
										{
											title: 'Breakeven Freight',
											key: ['EstimateResult', 'breakevenFreight'],
											type: 'currency',
											calculated: true,
											render: (v, row) => (
												v == null || row.Cargos.length !== 1 ?
													null :
													formatFreightRate(v, row.Cargos[0])
											),
										},
										{
											title: 'APS TCE /day',
											key: ['EstimateResult', 'apsTce'],
											type: 'currency',
											calculated: true,
											render: (v, row) => (
												v == null ? null : formatCurrency(v, row?.currency ?? 'USD')
											),
										},
									],
								},
								{
									title: 'Vessel',
									outlined: true,
									children: [
										{
											title: 'Name',
											key: 'vesselId',
											render: (vesselId, row) => (
												<VesselSelector
													disabled={isFixedRow(row)}
													data-shortcut="vessel"
													includeVesselIndex
													includeMarketVessels
													showNoVessel
													variant="outlined"
													value={vesselId}
													onChange={(newVesselId) => onUpdateGridEstimate(row.id, { vesselId: newVesselId })}
												/>
											),
										},
										{
											title: 'Vessel Cost',
											key: 'tcInRate',
											render: (vesselCosts, row) => (
												<NumericInput
													disabled={isFixedRow(row)}
													size="small"
													value={vesselCosts}
													placeholder="Rate"
													addonBefore={currencySymbol(row)}
													addonAfter={row?.vesselCostType === VesselCostType.PerDay ? '/d' : 'LS'}
													onChange={(e) => onUpdateGridEstimate(row.id, { tcInRate: Number(e) })}
												/>
											),
										},
										{
											title: 'Cost Unit',
											key: 'vesselCostType',
											render: (vesselCostType, row) => (
												<Select
													disabled={isFixedRow(row)}
													placeholder="Select unit"
													value={vesselCostType}
													size="small"
													dropdownStyle={{ width: 350 }}
													optionRender={(option) => (
														<Space size="small">
															{option.label}
														</Space>
													)}
													className={styles.select}
													showSearch
													options={[
														{
															label: '/day',
															value: VesselCostType.PerDay,
														},
														{
															label: 'Lumpsum',
															value: VesselCostType.Lumpsum,
														},
													]}
													onChange={(e) => onUpdateGridEstimate(row.id, {
														vesselCostType: e,
													})}
												/>
											),
										},
										{
											title: 'Add. Comm.',
											key: 'hireAddComm',
											render: (hireAddComm, row) => (
												<NumericInput
													disabled={isFixedRow(row)}
													size="small"
													value={hireAddComm}
													placeholder="Hire Add. Comm."
													addonAfter="%"
													onChange={(e) => onUpdateGridEstimate(row.id, {
														hireAddComm: Number(e),
													})}
												/>
											),
										},
										{
											title: 'Draft',
											getValue: (row) => getVesselParticular(row, 'summerDraftOverride', 'summerDraft'),
											render: (summerDraft, row) => (
												<NumericInput
													disabled={isFixedRow(row)}
													size="small"
													value={summerDraft}
													placeholder="Summer draft"
													addonAfter="m"
													onChange={(e) => onUpdateGridEstimate(row.id, {
														summerDraftOverride: Number(e),
													})}
												/>
											),
										},
										{
											title: 'DWT',
											getValue: (row) => getVesselParticular(row, 'dwtOverride', 'summerDWT'),
											render: (dwt, row) => (
												<NumericInput
													disabled={isFixedRow(row)}
													size="small"
													value={dwt}
													placeholder="Summer DWT"
													addonAfter="MT"
													onChange={(e) => onUpdateGridEstimate(row.id, {
														dwtOverride: Number(e),
													})}
												/>
											),
										},
										{
											title: 'TPC',
											getValue: (row) => getVesselParticular(row, 'tpcOverride', 'TPC'),
											render: (tpc, row) => (
												<NumericInput
													disabled={isFixedRow(row)}
													size="small"
													value={tpc}
													placeholder="TPC"
													addonAfter="MT"
													onChange={(e) => onUpdateGridEstimate(row.id, {
														tpcOverride: Number(e),
													})}
												/>
											),
										},
									],
								},
								{
									title: '',
									children: [
										{
											title: 'EUA Price',
											key: 'euaPriceOverride',
											render: (override, row) => {
												const value = override == null || override === 0 ?
													row.remoteEuaPrice ?? 0 :
													override;
												const euaDisabled = isFixedRow(row) || hasCargoRelet(row);

												return (
													<NumericInput
														size="small"
														disabled={euaDisabled}
														addonBefore={currencySymbol(row)}
														addonAfter={(
															<Button
																type="link"
																size="small"
																className={styles.addonButton}
																disabled={euaDisabled || hasPendingGridChanges}
																disabledTooltip={hasPendingGridChanges ? SAVE_FIRST_TOOLTIP : undefined}
																onClick={() => onRefreshEuaPrice(row.id)}
																icon={(
																	<FontAwesomeIcon
																		icon={faRefresh}
																		spin={refreshingEuaEstimateId === row.id}
																	/>
																)}
															/>
														)}
														value={round(value, 2)}
														placeholder="EUA Price"
														onChange={(e) => onUpdateGridEstimate(row.id, {
															euaPriceOverride: Number(e),
														})}
														data-testid="euaPrice"
													/>
												);
											},
										},
										{
											title: 'UKA Price',
											key: 'ukaPrice',
											render: (ukaPrice, row) => (
												<NumericInput
													size="small"
													disabled={isFixedRow(row)}
													addonBefore={currencySymbol(row)}
													value={ukaPrice == null ? undefined : round(ukaPrice, 2)}
													placeholder="UKA Price"
													onChange={(e) => onUpdateGridEstimate(row.id, {
														ukaPrice: Number(e),
													})}
													data-testid="ukaPrice"
												/>
											),
										},
										{
											title: 'Fuel EU',
											key: 'isFuelEU',
											render: (isFuelEU, row) => (
												<Select
													disabled={isFixedRow(row)}
													value={isFuelEU}
													size="small"
													// @ts-ignore This works because it's feature in rc-select
													showAction="focus"
													placeholder="Select Option"
													options={[
														{ label: 'Yes', value: true },
														{ label: 'No', value: false },
													]}
													onChange={(v) => onUpdateGridEstimate(row.id, {
														isFuelEU: v ?? false,
													})}
												/>
											),
										},
										{
											title: 'Misc. costs',
											key: 'miscCostTotal',
											render: (miscCostTotal, row) => {
												const costs = row.EstimateCosts ?? [];

												return (
													<NumericInput
														disabled={
															isFixedRow(row) ||
															costs.length > 1 ||
															costs.some((c) => c.unitType === VesselCostType.PerDay)
														}
														size="small"
														value={round(miscCostTotal ?? calcTotal(costs), 2)}
														addonBefore={currencySymbol(row)}
														addonAfter={(
															<Button
																type="link"
																size="small"
																className={styles.addonButton}
																disabled={isFixedRow(row) || hasPendingGridChanges}
																disabledTooltip={hasPendingGridChanges ? SAVE_FIRST_TOOLTIP : undefined}
																onClick={() => openEstimateModal('miscCosts', row.id)}
																icon={(<FontAwesomeIcon icon={faAdd} />)}
															/>
														)}
														placeholder="Misc. costs"
														onChange={(e) => onUpdateGridEstimate(row.id, {
															miscCostTotal: Number(e),
														})}
													/>
												);
											},
										},
										...bunkerFuelGrades.flatMap((fuelGrade) => {
											const getBunkerValue = (row: GridRow) => (
												getCommencementBunkerValues(row?.commencementBunkers)
													.find((b) => b.fuelGrade === fuelGrade)
											);

											const isDisabled = (row: GridRow, hasMultipleParcels: boolean) => (
												isFixedRow(row) ||
												hasCargoRelet(row) ||
												hasMultipleParcels
											);

											return [
												{
													title: `${fuelGrade} price`,
													render: (_v: unknown, row: GridRow) => {
														const bunkerValue = getBunkerValue(row);

														if (bunkerValue == null) {
															return ' - ';
														}

														return (
															<NumericInput
																disabled={isDisabled(row, bunkerValue.numberOfParcels > 1)}
																size="small"
																data-shortcut="bunker-price"
																value={bunkerValue.weightedPricePerTon ?? 0}
																addonBefore={currencySymbol(row)}
																placeholder={`${fuelGrade} price`}
																addonAfter="/MT"
																onChange={(e) => onChangeCommencementBunker(row, fuelGrade, {
																	pricePerTon: Number(e) ?? 0,
																})}
															/>
														);
													},
												},
												{
													title: `${fuelGrade} qty`,
													render: (_v: unknown, row: GridRow) => {
														const bunkerValue = getBunkerValue(row);

														if (bunkerValue == null) {
															return ' - ';
														}

														return (
															<NumericInput
																disabled={isDisabled(row, bunkerValue.numberOfParcels > 1)}
																size="small"
																value={bunkerValue.totalQuantity ?? 0}
																placeholder={`${fuelGrade} qty`}
																addonAfter="MT"
																onChange={(e) => onChangeCommencementBunker(row, fuelGrade, {
																	quantity: Number(e) ?? 0,
																})}
															/>
														);
													},
												},
											];
										}),
										{
											title: 'Cons',
											render: (_c, row) => (
												<Button
													disabled={isFixedRow(row) || row.vesselId == null || hasPendingGridChanges}
													disabledTooltip={hasPendingGridChanges ? SAVE_FIRST_TOOLTIP : undefined}
													loading={
														fetchingGroupVessels &&
														selectedVesselId === row.vesselId &&
														selectedVessel == null
													}
													onClick={() => setSelectedVesselId(row.vesselId)}
													icon={(<FontAwesomeIcon icon={faGasPump} />)}
													className={styles.fullWidth}
												>
													Open Vessel Speed & Consumption
												</Button>
											),
										},
										{
											title: 'ROBs & prices',
											render: (_c, row) => (
												<Button
													disabled={isFixedRow(row) || hasPendingGridChanges}
													disabledTooltip={hasPendingGridChanges ? SAVE_FIRST_TOOLTIP : undefined}
													type="primary"
													icon={(<FontAwesomeIcon icon={faGasPump} />)}
													className={styles.fullWidth}
													data-shortcut="bunker-price"
													onClick={() => {
														setSelectedEstimateId(row.id);
														setShowRobsModal(true);
													}}
												>
													Set ROBs & prices
												</Button>
											),
										},
										{
											title: 'Prev. contract',
											render: (_c, row) => {
												const rowVessel = getRowVessel(row);
												const hasLinkableVessel = (
													rowVessel?.ownershipType === VesselOwnershipTypes.OWNED ||
													rowVessel?.ownershipType === VesselOwnershipTypes.TC_IN
												);

												return (
													<Button
														disabled={
															!hasLinkableVessel ||
															hasCargoRelet(row) ||
															hasPendingGridChanges
														}
														disabledTooltip={hasPendingGridChanges ? SAVE_FIRST_TOOLTIP : undefined}
														className={styles.fullWidth}
														onClick={() => {
															setSelectedEstimateId(row.id);
															setShowLinkModal(true);
														}}
													>
														Import prev. contract
													</Button>
												);
											},
										},
										{
											title: '',
											render: (_c, row) => (
												<Flex gap={4}>
													<Button
														type="primary"
														icon={(<PlusOutlined />)}
														className={styles.fullWidth}
														disabled={isFixedRow(row) || hasCargoRelet(row) || hasPendingGridChanges}
														disabledTooltip={hasPendingGridChanges ? SAVE_FIRST_TOOLTIP : undefined}
														onClick={() => onCreateCargo(row.id)}
														data-testid="gridNewCargo"
													>
														New Cargo
													</Button>
													<Button
														type="primary"
														icon={(<FontAwesomeIcon icon={faRightLeft} />)}
														className={styles.fullWidth}
														disabled={
															isFixedRow(row) ||
															hasCargoRelet(row) ||
															row.gridCargos.length !== 1 ||
															hasPendingGridChanges
														}
														disabledTooltip={hasPendingGridChanges ? SAVE_FIRST_TOOLTIP : undefined}
														onClick={() => onCreateCargoRelet(row.id)}
														data-testid="gridReletCargo"
													>
														Relet cargo
													</Button>
												</Flex>
											),
										},
									],
								},
								{
									title: 'Voyage',
									children: [
										{
											title: 'Open Port',
											key: 'openPort',
											render: (_op, row) => {
												const ballastLegChanges = pendingBallastLegChanges[row.id];
												const value = ballastLegChanges?.port !== undefined ?
													ballastLegChanges.port :
													getBallastLeg(row)?.port?.id;

												return (
													<Select
														disabled={isFixedRow(row)}
														size="small"
														value={value}
														// @ts-ignore This works because it's feature in rc-select
														showAction="focus"
														showSearch
														placeholder="Open Port"
														options={portOptions}
														onChange={(portId) => onUpdateGridBallastLeg(row.id, { port: portId })}
													/>
												);
											},
										},
										{
											title: 'Open Date',
											key: 'openDate',
											render: (_od, row) => {
												const ballastLeg = getBallastLeg(row);

												const value =
													fromISOTimezone(
														ballastLeg?.departureDate?.toISOString(true),
														estimate?.selectedTimeFormat ?? TimeZoneFormat.UTC,
														ballastLeg?.port?.timezone,
													);

												return (
													<Flex align="center" className={styles.inlineRow}>
														<DatePicker
															fullWidth
															key={`openDate-${value?.toISOString()}`}
															time
															showTimezone={false}
															disabled={isFixedRow(row)}
															disableTimezone
															defaultValue={value}
															size="small"
															onChange={(d) => onUpdateGridBallastLeg(row.id, {
																departureDate: toISOTimezoneInstant(
																	d as Moment,
																	estimate?.selectedTimeFormat ?? TimeZoneFormat.UTC,
																	ballastLeg?.port?.timezone,
																),
															})}
														/>
														<span style={{ fontSize: 12, opacity: 0.7, marginLeft: 10 }}>
															{timeZoneOffsetLabel(
																value,
																ballastLeg?.port?.timezone,
																estimate?.selectedTimeFormat ?? TimeZoneFormat.UTC,
															)}
														</span>
													</Flex>
												);
											},
										},
										{
											title: 'Ports',
											align: 'left',
											render: (_, row) => {
												const portRotation = sortByDates(
													[...(row?.EstimatePortRotationEntries ?? [])],
													'departureDate',
													{ newestFirst: false },
												);

												return (
													<Flex justify="space-between" align="center" gap={8}>
														<span>
															{portRotation.map((pr, i) => (
																<span key={pr.id}>
																	{pr.port?.name ?? 'Unknown Port'}
																	{portRotation[i + 1] != null && (
																		<FontAwesomeIcon className={styles.portArrow} icon={faArrowRight} />
																	)}
																</span>
															))}
														</span>
														<Button
															size="small"
															disabled={hasPendingGridChanges}
															disabledTooltip={SAVE_FIRST_TOOLTIP}
															onClick={(e) => {
																portsTriggerRef.current = e?.currentTarget ?? null;
																openEstimateModal('ports', row.id);
															}}
															data-testid="openPorts"
															data-shortcut="first-port"
														>
															Ports
														</Button>
													</Flex>
												);
											},
										},
									],
								},
								...Array.from({ length: maxCargoCount }, (_, index) => getCargoGroup(index)),
							]}
						/>
					</div>
				</Card>
			</Col>
		</Row>
	);
};

export default GridView;
